/**
 * Sponsorship Service
 * 
 * This is a thin wrapper around adminApi.js that provides a cleaner
 * interface for sponsorship-related operations. All actual API calls
 * are delegated to adminApi.js which uses the correct axios instance
 * and configuration.
 */

import {
  getSponsors,
  createSponsor,
  updateSponsor,
  getSponsorById,
  getContracts,
  createContract,
  getContractById,
  updateContract,
  runFeasibilityCheck,
  activateContract,
  pauseContract,
  resumeContract,
  completeContract,
  getCategories,
  getCategoryById,
  createCategory,
  updateCategory,
  queryInventory,
  getSponsorDeliveryReport,
  getUnfilledSlotsReport,
  runWeeklyAssignmentJob,
  uploadMedia,
  getDashboardOverview,
  getWeeklyDeliveryReport,
  getContractValidationReport,
  getSponsorSummaryReport,
  getInventoryUtilizationReport,
  getUsageSignalReport,
} from './adminApi';

// ============================================
// SPONSORS API
// ============================================

export const sponsorsApi = {
  /**
   * List all sponsors
   * @param {Object} params - Query parameters
   */
  list: async (params = {}) => {
    return await getSponsors(params);
  },

  /**
   * Get sponsor by ID
   * @param {string} sponsorId - Sponsor ID
   */
  get: async (sponsorId) => {
    return await getSponsorById(sponsorId);
  },

  /**
   * Create new sponsor
   * @param {Object} sponsorData - Sponsor data
   */
  create: async (sponsorData) => {
    return await createSponsor(sponsorData);
  },

  /**
   * Update sponsor
   * @param {string} sponsorId - Sponsor ID
   * @param {Object} updateData - Update data
   */
  update: async (sponsorId, updateData) => {
    return await updateSponsor(sponsorId, updateData);
  },

  /**
   * Delete sponsor (soft delete by setting is_active: false)
   * @param {string} sponsorId - Sponsor ID
   */
  delete: async (sponsorId) => {
    return await updateSponsor(sponsorId, { is_active: false });
  },
};

// ============================================
// CONTRACTS API
// ============================================

export const contractsApi = {
  /**
   * List all contracts
   * @param {Object} params - Query parameters
   */
  list: async (params = {}) => {
    return await getContracts(params);
  },

  /**
   * Get contract by ID
   * @param {string} contractId - Contract ID
   */
  get: async (contractId) => {
    return await getContractById(contractId);
  },

  /**
   * Create new contract (draft status)
   * @param {Object} contractData - Contract data
   */
  create: async (contractData) => {
    return await createContract(contractData);
  },

  /**
   * Update contract (draft only)
   * @param {string} contractId - Contract ID
   * @param {Object} updateData - Update data
   */
  update: async (contractId, updateData) => {
    return await updateContract(contractId, updateData);
  },

  /**
   * Delete contract
   * @param {string} contractId - Contract ID
   */
  delete: async (contractId) => {
    // Soft delete by updating status to cancelled
    return await updateContract(contractId, { status: 'cancelled' });
  },

  /**
   * Check contract feasibility
   * @param {string} contractId - Contract ID
   */
  checkFeasibility: async (contractId) => {
    return await runFeasibilityCheck(contractId);
  },

  /**
   * Activate contract
   * @param {string} contractId - Contract ID
   */
  activate: async (contractId) => {
    return await activateContract(contractId);
  },

  /**
   * Pause contract
   * @param {string} contractId - Contract ID
   */
  pause: async (contractId) => {
    return await pauseContract(contractId);
  },

  /**
   * Resume contract
   * @param {string} contractId - Contract ID
   */
  resume: async (contractId) => {
    return await resumeContract(contractId);
  },

  /**
   * Complete contract
   * @param {string} contractId - Contract ID
   */
  complete: async (contractId) => {
    return await completeContract(contractId);
  },
};

// ============================================
// CATEGORIES API
// ============================================

export const categoriesApi = {
  /**
   * List all categories
   */
  list: async () => {
    return await getCategories();
  },

  /**
   * Get category by ID
   * @param {string} categoryId - Category ID
   */
  get: async (categoryId) => {
    return await getCategoryById(categoryId);
  },

  /**
   * Create new category
   * @param {Object} categoryData - Category data
   */
  create: async (categoryData) => {
    return await createCategory(categoryData);
  },

  /**
   * Update category
   * @param {string} categoryId - Category ID
   * @param {Object} updateData - Update data
   */
  update: async (categoryId, updateData) => {
    return await updateCategory(categoryId, updateData);
  },

  /**
   * Delete category (soft delete by setting is_active: false)
   * @param {string} categoryId - Category ID
   */
  delete: async (categoryId) => {
    return await updateCategory(categoryId, { is_active: false });
  },
};

// ============================================
// INVENTORY API
// ============================================

export const inventoryApi = {
  /**
   * Query inventory availability
   * @param {Object} queryData - Query parameters
   */
  query: async (queryData) => {
    return await queryInventory(queryData);
  },
};

// ============================================
// REPORTS API
// ============================================

export const reportsApi = {
  /**
   * Get sponsor delivery metrics
   * @param {string} sponsorId - Sponsor ID
   */
  getSponsorDelivery: async (sponsorId, params = {}) => {
    return await getSponsorDeliveryReport(sponsorId, params);
  },

  /**
   * Get unfilled slots report
   * @param {Object} params - Query parameters
   */
  getUnfilledSlots: async (params = {}) => {
    return await getUnfilledSlotsReport(params);
  },

  /**
   * Get weekly delivery report
   * @param {string} sponsorId - Sponsor ID
   * @param {number} year - Calendar year
   * @param {number} week - Calendar week (1-52)
   */
  getWeeklyDelivery: async (sponsorId, year, week) => {
    return await getWeeklyDeliveryReport(sponsorId, year, week);
  },

  /**
   * Get contract validation report
   * @param {string} contractId - Contract ID
   * @param {boolean} includeWeeklyBreakdown - Include weekly breakdown
   */
  getContractValidation: async (contractId, includeWeeklyBreakdown = false) => {
    return await getContractValidationReport(contractId, includeWeeklyBreakdown);
  },

  /**
   * Get sponsor summary report
   * @param {string} sponsorId - Sponsor ID
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   */
  getSponsorSummary: async (sponsorId, startDate, endDate) => {
    return await getSponsorSummaryReport(sponsorId, startDate, endDate);
  },

  /**
   * Get inventory utilization report
   * @param {Object} params - Query parameters
   */
  getInventoryUtilization: async (params) => {
    return await getInventoryUtilizationReport(params);
  },

  /**
   * Get usage signal report
   * @param {string} sponsorId - Sponsor ID
   * @param {number} year - Calendar year
   * @param {number} week - Calendar week (1-52)
   */
  getUsageSignal: async (sponsorId, year, week) => {
    return await getUsageSignalReport(sponsorId, year, week);
  },
};

// ============================================
// ASSIGNMENTS API
// ============================================

export const assignmentsApi = {
  /**
   * Run weekly assignment job
   * @param {Object} jobData - Job parameters (optional)
   */
  runWeeklyJob: async (jobData = {}) => {
    return await runWeeklyAssignmentJob(jobData);
  },
};

// ============================================
// MEDIA API
// ============================================

export const mediaApi = {
  /**
   * Upload media file
   * @param {FormData} formData - File data
   */
  upload: async (formData) => {
    return await uploadMedia(formData);
  },
};

// ============================================
// DASHBOARD API
// ============================================

export const dashboardApi = {
  /**
   * Get dashboard overview with key metrics
   */
  getOverview: async () => {
    return await getDashboardOverview();
  },
};

// Export all APIs as a single object
export const sponsorshipService = {
  sponsors: sponsorsApi,
  contracts: contractsApi,
  categories: categoriesApi,
  inventory: inventoryApi,
  reports: reportsApi,
  assignments: assignmentsApi,
  media: mediaApi,
  dashboard: dashboardApi,
};

export default sponsorshipService;
